import { cx, focusRing } from "components/Graphs/utils";
import * as Tooltip from "@radix-ui/react-tooltip";
import { ExternalLink } from "lucide-react";
import React from "react";

import type { ShellNavItem } from "../types";

export interface ShellRailItemProps {
  item: ShellNavItem;
  active?: boolean;
  onNavigate: (item: ShellNavItem) => void;
  mobile?: boolean;
  expanded?: boolean;
  className?: string;
}

export const ShellRailItem: React.FC<ShellRailItemProps> = ({
  item,
  active = false,
  onNavigate,
  mobile = false,
  expanded = false,
  className,
}) => {
  const Icon = item.icon;
  const tooltipText =
    item.disabled && item.disabledReason ? item.disabledReason : item.label;
  const showTooltip = mobile || !expanded;
  const showInlineBadge = Boolean(item.badge && !mobile && expanded);
  const showFloatingBadge = Boolean(item.badge && (mobile || !expanded));
  const showExternalCue = Boolean(item.external && !mobile && expanded);

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (item.disabled) {
      event.preventDefault();
      return;
    }

    if (!item.external) event.preventDefault();
    onNavigate(item);
  };

  const navigationLink = (
    <a
      href={item.href}
      target={item.external ? "_blank" : undefined}
      rel={item.external ? "noreferrer" : undefined}
      onClick={handleClick}
      aria-label={item.badge ? `${item.label}, ${item.badge}` : item.label}
      aria-current={active ? "page" : undefined}
      aria-disabled={item.disabled ? "true" : undefined}
      data-shell-nav-item={item.id}
      className={cx(
        "group relative flex shrink-0 items-center justify-center border text-zinc-500 transition-[background-color,border-color,color,box-shadow,transform] duration-150",
        mobile
          ? "h-12 min-w-12 flex-1 rounded-md"
          : expanded
            ? "h-11 w-full justify-start gap-2 rounded-md pl-4 pr-3"
            : "size-11 rounded-md",
        active
          ? "border-transparent bg-transparent text-highlight shadow-none"
          : "border-transparent hover:text-zinc-100",
        item.disabled &&
          "cursor-not-allowed border-transparent text-zinc-700 shadow-none hover:border-transparent hover:bg-transparent hover:text-zinc-700",
        "motion-reduce:transition-none",
        focusRing,
        className,
      )}
    >
      <Icon
        className={cx(
          "shrink-0",
          mobile ? "size-5" : expanded ? "size-4" : "size-[1.125rem]",
        )}
        strokeWidth={1.65}
        aria-hidden="true"
      />
      {!mobile && expanded ? (
        <span className="flex min-w-0 flex-1 items-center gap-2">
          <span className="min-w-0 truncate text-sm font-medium">
            {item.label}
          </span>
          {showInlineBadge ? (
            <span className="shrink-0 rounded-full border border-current bg-white/10 px-1.5 py-0.5 text-[0.625rem] font-semibold uppercase leading-none">
              {item.badge}
            </span>
          ) : null}
          {showExternalCue ? (
            <ExternalLink
              className="size-3 shrink-0 text-current opacity-80"
              strokeWidth={1.9}
              data-shell-external-indicator
              aria-hidden="true"
            />
          ) : null}
        </span>
      ) : null}
      {showFloatingBadge ? (
        <span className="absolute right-0 top-0 rounded-full border border-current bg-[#090c0a] px-1 text-[0.5rem] font-bold uppercase leading-3 shadow-[0_2px_8px_rgba(0,0,0,0.3)]">
          {item.badge}
        </span>
      ) : null}
      {active ? (
        <span
          className={cx(
            "absolute",
            mobile
              ? "bottom-0.5 left-1/2 h-0.5 w-5 -translate-x-1/2 rounded-full bg-highlight"
              : expanded
                ? "left-0 inset-y-0 border-l-[3px] border-l-highlight"
                : "-left-2.5 inset-y-0 border-l-[3px] border-l-highlight",
          )}
          data-shell-active-indicator
          aria-hidden="true"
        />
      ) : null}
    </a>
  );

  if (!showTooltip) return navigationLink;

  return (
    <Tooltip.Provider delayDuration={100}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>{navigationLink}</Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            side={mobile ? "top" : "right"}
            sideOffset={mobile ? 8 : 10}
            collisionPadding={12}
            className="z-[100] max-w-72 rounded border border-zinc-700 bg-zinc-950 px-3 py-2 text-xs leading-4 text-zinc-100 shadow-2xl"
          >
            {tooltipText}
            <Tooltip.Arrow className="fill-zinc-950" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
};

ShellRailItem.displayName = "ShellRailItem";

export default ShellRailItem;
